import React, { useMemo, useEffect, useCallback, useState } from 'react';
import { DataGrid } from '@material-ui/data-grid';
import {
  useMediaQuery,
  makeStyles,
  Typography,
  Checkbox,
  Tooltip,
} from '@material-ui/core';
import HelpIcon from '@material-ui/icons/Help';
import PriceInput from '../../PriceInput';

const useStyles = makeStyles((theme) => ({
  dataGridRoot: {
    '&.MuiDataGrid-root': {
      border: 'none',
    },
  },
  checkbox: {
    '& .MuiIconButton-label': {
      color: theme.palette.primary.main,
    },
  },
  disabledText: {
    '& .MuiInputBase-input': {
      color: theme.palette.grey[500],
    },
    color: theme.palette.grey[500],
  },
  bold: {
    '& .MuiInputBase-input': {
      fontWeight: 700,
    },
  },
  inputHeight: {
    '& .MuiInputBase-input': {
      height: '2.19em',
    },
  },
  icon: {
    fontSize: 16,
  },
}));

const sortModel = [
  {
    field: 'price_sheet_name_is_default',
    sort: 'desc',
  },
  {
    field: 'id',
    sort: 'asc',
  },
];

const EditablePriceGrid = (props) => {
  const { record, priceSheets, prices, setPrices } = props;
  const classes = useStyles();
  const [areAllChecked, setAreAllChecked] = useState(false);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  // Re-evaluate whether the 'areAllChecked' checkbox should be checked
  useEffect(() => {
    if (prices.every((p) => p.is_checked === true)) {
      setAreAllChecked(true);
    } else {
      setAreAllChecked(false);
    }
  }, [prices]);

  // Render a row for each price sheet, even if price doesn't exist
  useEffect(() => {
    const priceSheetIdsFromPrices = prices.map((price) => price.price_sheet_id);
    const missingPriceSheets = priceSheets.filter(
      (sheet) => !priceSheetIdsFromPrices.includes(sheet.id),
    );
    // If SO Edit and there is no price for default price sheet, is_checked = false
    // Else, if SO Create, automatically set the default row checked
    const missingPricesObjList = missingPriceSheets.map((mp) => ({
      id: `${mp.id}/${record.service_offering_id || 'new'}`,
      service_offering_id: record.service_offering_id || null,
      price_sheet_id: mp.id,
      price_sheet_name: mp.price_sheet_name,
      parts_cost_dollars: 0.0,
      labor_cost_dollars: 0.0,
      is_checked: record.service_offering_id
        ? false
        : mp.price_sheet_name === 'Default',
    }));
    setPrices((prevState) => {
      const updated = [...prevState];
      updated.push(...missingPricesObjList);
      return updated;
    });
  }, [prices.length, priceSheets]);

  const renderCheckboxHeader = useCallback(() => {
    const handleChange = (e) => {
      setPrices((prevState) => {
        const updated = [...prevState].map((price) => {
          const updatedPrice = { ...price };
          updatedPrice.is_checked = e.target.checked;
          return updatedPrice;
        });
        return updated;
      });
      setAreAllChecked(e.target.checked);
    };

    return (
      <div>
        <Tooltip placement="right" title="Is Active?">
          <Checkbox
            className={classes.checkbox}
            checked={areAllChecked}
            onChange={handleChange}
            helperText={false}
            label=""
          />
        </Tooltip>
      </div>
    );
  }, [areAllChecked]);

  const renderCheckboxCells = useCallback(
    ({ row: currentRow, field }) => {
      const thisPrice = prices.find((el) => el.id === currentRow.id);

      const handleChange = (e) => {
        setPrices((prevState) => {
          const updated = [...prevState];
          const thisPriceIndex = updated.findIndex(
            (el) => el.id === currentRow.id,
          );
          updated[thisPriceIndex][field] = e.target.checked;
          return updated;
        });
      };

      return (
        <Checkbox
          className={classes.checkbox}
          checked={thisPrice[field]}
          onChange={handleChange}
          helperText={false}
          label=""
        />
      );
    },
    [prices],
  );

  const renderPriceSheetNameCells = useCallback(
    ({ value: priceSheetName, row: currentRow }) => {
      if (priceSheetName === 'Default') {
        return (
          <Typography
            className={currentRow.is_checked ? null : classes.disabledText}
          >
            <strong>{priceSheetName}</strong>
            {'  '}
            <Tooltip
              placement="right"
              title={
                <>
                  <p>
                    This is the default price sheet that will be used for all
                    vehicles. You can set vehicle-specific overrides with the
                    price rows below.
                  </p>
                  <p>
                    In order to allow customers to book this service only for
                    specific vehicle types (for example, Small Vehicles),
                    deselect default price sheet and add vehicle-specific prices
                    below.
                  </p>
                </>
              }
            >
              <HelpIcon className={classes.icon} />
            </Tooltip>
          </Typography>
        );
      }
      return (
        <Typography
          className={currentRow.is_checked ? null : classes.disabledText}
        >
          {priceSheetName}
        </Typography>
      );
    },
    [prices],
  );

  const renderPriceInputCells = useCallback(
    ({ field, row: currentRow }) => {
      const thisPrice = prices.find((el) => el.id === currentRow.id);

      const handleChange = (e) => {
        setPrices((prevState) => {
          const updated = [...prevState];
          const thisPriceIndex = updated.findIndex(
            (el) => el.id === currentRow.id,
          );
          updated[thisPriceIndex] = {
            ...updated[thisPriceIndex],
            [field]: e.target.value,
            is_checked: true,
          };
          return updated;
        });
      };

      // If currentRow is checked, make sure it has values
      const handleBlur = () => {
        if (
          currentRow.parts_cost_dollars === '' ||
          currentRow.labor_cost_dollars === ''
        ) {
          const currentRowCopy = { ...currentRow };
          if (currentRow.parts_cost_dollars === '') {
            currentRowCopy.parts_cost_dollars = 0;
          }
          if (currentRow.labor_cost_dollars === '') {
            currentRowCopy.labor_cost_dollars = 0;
          }
          setPrices((prevState) => {
            const updated = [...prevState];
            const thisPriceIndex = updated.findIndex(
              (el) => el.id === currentRow.id,
            );
            updated[thisPriceIndex] = currentRowCopy;
            return updated;
          });
        }
      };

      const defaultRow = prices.find((el) => el.price_sheet_name === 'Default');
      // If currentRow is not checked and Default row is, set price to the default price
      if (!currentRow.is_checked) {
        if (defaultRow?.is_checked && currentRow[field] !== defaultRow[field]) {
          setPrices((prevState) => {
            const updated = [...prevState];
            const thisPriceIndex = updated.findIndex(
              (el) => el.id === currentRow.id,
            );
            updated[thisPriceIndex][field] = defaultRow[field];
            return updated;
          });
          // Else if currentRow is checked but not using Default, set empty
        } else if (!defaultRow?.is_checked && currentRow[field] !== '') {
          setPrices((prevState) => {
            const updated = [...prevState];
            const thisPriceIndex = updated.findIndex(
              (el) => el.id === currentRow.id,
            );
            updated[thisPriceIndex][field] = '';
            return updated;
          });
        }
      }

      return (
        <PriceInput
          variant="standard"
          onChange={handleChange}
          onBlur={handleBlur}
          value={thisPrice[field]}
          source={field}
          label=""
          className={`${currentRow.is_checked ? '' : classes.disabledText} ${
            currentRow.price_sheet_name === 'Default' ? classes.bold : ''
          } ${classes.inputHeight}`}
        />
      );
    },
    [prices],
  );

  const columns = useMemo(() => {
    return [
      { field: 'id', headerName: 'ID', width: 100, hide: true },
      {
        field: 'price_sheet_name_is_default',
        headerName: 'Price Sheet Name Is Default',
        valueGetter: (params) => params.row.price_sheet_name === 'Default',
        width: 100,
        hide: true,
      },
      {
        field: 'is_checked',
        headerName: 'Is Active',
        width: 70,
        sortable: false,
        renderHeader: renderCheckboxHeader,
        renderCell: (params) => renderCheckboxCells(params),
      },
      {
        field: 'price_sheet_name',
        headerName: 'Price Sheet Name',
        flex: isSmallScreen ? false : 2,
        width: isSmallScreen ? 200 : false,
        sortable: false,
        renderCell: (params) => renderPriceSheetNameCells(params),
      },
      {
        field: 'parts_cost_dollars',
        headerName: 'Parts Charge',
        flex: isSmallScreen ? false : 1.5,
        width: isSmallScreen ? 150 : false,
        sortable: false,
        renderCell: (params) => renderPriceInputCells(params),
      },
      {
        field: 'labor_cost_dollars',
        headerName: 'Labor Charge',
        flex: isSmallScreen ? false : 1.5,
        width: isSmallScreen ? 150 : false,
        sortable: false,
        renderCell: (params) => renderPriceInputCells(params),
      },
    ];
  }, [prices, isSmallScreen, areAllChecked]);

  return (
    <div>
      <DataGrid
        className={classes.dataGridRoot}
        rows={prices}
        columns={columns}
        pageSize={15}
        disableColumnMenu
        disableSelectionOnClick
        autoHeight
        hideFooter={prices.length <= 15}
        sortModel={sortModel}
      />
    </div>
  );
};

export default EditablePriceGrid;
